/**
 * Branded PDF export for any CrudConfig + row pair.
 *
 * Used by the "Download PDF" button on every report's DetailDialog.
 * Shared template means a Job Card PDF and an Asset PDF land with the
 * same look, just different fields. Generation is fully client-side —
 * no server round-trip — so it works on every deploy target (Render
 * Starter included) without adding a headless-Chrome dependency.
 *
 * Tech: pdfmake. ~600KB gzipped including fonts; the vfs_fonts module
 * is a hard dep for the bundled Roboto family.
 */
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import type { CrudConfig, FieldConfig } from '@/components/shared/crud-page';
import { fmtDate, fmtDateTime } from './format-date';

// pdfmake's CJS bundle puts the VFS map under different shapes depending
// on build version — older versions expose `pdfFonts.pdfMake.vfs`,
// newer ones publish the map at the top level. The OR fallback covers
// both without needing to lock to a specific build.
(pdfMake as any).vfs = (pdfFonts as any)?.pdfMake?.vfs ?? (pdfFonts as any)?.vfs ?? (pdfFonts as any);

const BRAND = {
  name: 'Atlas Fleet Maintenance',
  // Mirrors the app theme — `--primary: 0 72% 51%` in globals.css, which
  // is the saturated red Tailwind calls red-600 (#DC2626). The slate
  // greys are kept for body text + labels so the PDF stays legible
  // (large blocks of red text read as alarming). Tweak here once,
  // applies everywhere.
  primary: '#0F172A',  // slate-900 — header text + values
  accent:  '#DC2626',  // app primary — section headers + subtitle rule
  accentDark: '#B91C1C', // red-700 — reserved for emphasis blocks
  muted:   '#64748B',  // slate-500 — labels + footer
  border:  '#E2E8F0',  // slate-200 — section separators
};

// Lift the dotted-path getter and ref-label resolver into this module so
// the PDF text matches what the on-screen DetailDialog shows. Kept
// duplicate (rather than imported from detail-dialog.tsx) so this file
// stays free of React + JSX imports and pdfmake doesn't end up in the
// React render path.
function get(obj: any, path: string): any {
  return path.split('.').reduce((a, k) => (a == null ? a : a[k]), obj);
}

/** Plain-text render of a field's value. Mirrors `ReadOnlyField` in
 *  `detail-dialog.tsx` but emits strings only — pdfmake doesn't take
 *  React elements. */
function renderValue(f: FieldConfig, row: any): string {
  const raw = get(row, f.name);
  if (raw === null || raw === undefined || raw === '') return '—';
  if (f.type === 'boolean') return raw === true ? 'Yes' : raw === false ? 'No' : '—';
  if (f.type === 'date') return fmtDate(raw);
  if (f.type === 'datetime') return fmtDateTime(raw);
  if (f.type === 'select') {
    const opt = f.options?.find((o) => o.value === raw);
    return opt?.label ?? String(raw);
  }
  if (f.type === 'ref') {
    if (typeof raw === 'string') return f.refValueField ? raw : '—';
    const labelKey = f.refLabel || 'name';
    return (raw as any)?.[labelKey]
        ?? (raw as any)?.fullName
        ?? (typeof (raw as any)?.name === 'string' ? (raw as any).name : null)
        ?? (raw as any)?.group
        ?? '—';
  }
  if (f.type === 'multi-ref') {
    if (!Array.isArray(raw) || raw.length === 0) return '—';
    const labelKey = f.refLabel || 'name';
    const names = raw
      .map((v: any) => {
        if (typeof v === 'string') return null;
        const k = v?.[labelKey];
        if (typeof k === 'string') return k;
        if (typeof v?.fullName === 'string') return v.fullName;
        if (typeof v?.name === 'string') return v.name;
        return null;
      })
      .filter((x): x is string => x !== null);
    return names.length ? names.join(', ') : '—';
  }
  if (f.type === 'address' || f.type === 'address-search') {
    const a = raw ?? {};
    const lines = [
      [a.addressLine1, a.addressLine2].filter(Boolean).join(' '),
      [a.city, a.region, a.postalCode].filter(Boolean).join(' '),
      a.country,
    ].filter(Boolean);
    return lines.length ? lines.join('\n') : '—';
  }
  if (f.type === 'contact') {
    const c = raw ?? {};
    const name = [c.prefix, c.firstName, c.lastName].filter(Boolean).join(' ');
    return name || '—';
  }
  if (f.type === 'string-array') {
    const list = Array.isArray(raw) ? raw.filter(Boolean) : [];
    return list.length ? list.join(', ') : '—';
  }
  if (f.type === 'multi-select') {
    const list: string[] = Array.isArray(raw) ? raw : [];
    if (list.length === 0) return '—';
    return list.map((v) => f.options?.find((o) => o.value === v)?.label ?? v).join(', ');
  }
  if (f.type === 'number') {
    return typeof raw === 'number' ? raw.toLocaleString() : String(raw);
  }
  if (f.type === 'image' || f.type === 'file') {
    // Embedding the binary would need an async fetch + base64; skip for
    // v1 and just show the filename / placeholder.
    return typeof raw === 'string' ? raw : '—';
  }
  return String(raw);
}

/** Best-effort identifier for the row — mirrors the DetailDialog
 *  subtitle chain so the PDF lands with the same headline. */
function rowSubtitle(row: any): string {
  return row?.fleetNumber
    ?? row?.fullName
    ?? (typeof row?.name === 'string' ? row.name : null)
    ?? row?.materialName
    ?? row?.itemCode
    ?? row?.code
    ?? row?.registrationNumber
    ?? row?.jobSheetNumber
    ?? row?.jobNumber
    ?? row?.requestNumber
    ?? row?.orderNumber
    ?? row?.invoiceNumber
    ?? row?.title
    ?? row?.id
    ?? '';
}

/** Safe filename from the subtitle — strips characters Windows / OSX
 *  block in filenames and collapses whitespace. */
function safeFilename(parts: string[]): string {
  return parts
    .filter(Boolean)
    .join(' - ')
    .replace(/[\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, ' ')
    .trim() || 'record';
}

/** Pair field config with its rendered value, grouping by section so
 *  the PDF mirrors the on-screen view's grouping. */
function groupFields(fields: FieldConfig[]) {
  const out: Array<{ section: string | null; rows: Array<{ label: string; value: string; full: boolean }> }> = [];
  for (const f of fields) {
    const section = f.section ?? null;
    const last = out[out.length - 1];
    const row = {
      label: f.label,
      value: renderValue(f, undefined),  // placeholder; replaced below
      full: f.type === 'address' || f.type === 'address-search' || f.type === 'textarea' || f.type === 'image' || f.type === 'file',
    };
    if (!last || last.section !== section) out.push({ section, rows: [row] });
    else last.rows.push(row);
  }
  return out;
}

/** Extra blocks the caller can pass alongside the form fields — used by
 *  reports that have meaningful child data (e.g. Service History's
 *  checklist answers + uploaded photos) so the PDF mirrors what the
 *  detail panel + technician's job-card screen showed. Caller pre-fetches
 *  to keep this module free of api / S3 imports. */
export interface PdfExtras {
  /** Ordered checklist answers — `task` is the question, `answer` is the
   *  tech's pick ("OK" / "Not OK" / etc.), `comments` is free text. */
  checklistAnswers?: Array<{
    task: string | null;
    answer: string | null;
    comments: string | null;
  }>;
  /** Photos to embed. `dataUrl` must be a base64 data: URI (the binary
   *  fetched + base64-encoded by the caller). pdfmake's `image` field
   *  takes a data URL directly. */
  photos?: Array<{ caption: string; dataUrl: string }>;
}

/** Build the pdfmake document definition from the report config + a
 *  single row. Branded header + two-column grouped field grid + page
 *  footer with timestamp and page numbers. */
function buildDocDefinition(config: CrudConfig, row: any, extras?: PdfExtras) {
  const subtitle = rowSubtitle(row);
  const generatedAt = fmtDateTime(new Date());
  const groups = groupFields(config.fields.filter((f) => f.type !== 'custom'));
  // Rebuild values for real (groupFields was just the shape pass).
  for (const g of groups) {
    g.rows = g.rows.map((r) => {
      const field = config.fields.find((f) => f.label === r.label)!;
      return { ...r, value: renderValue(field, row) };
    });
  }

  // Each section -> a stack of: section header (line + label) + a two-
  // column table of { label, value } rows. `full` flagged rows take the
  // whole row (textarea, address, image — the same things DetailDialog
  // expands to col-span-2 on screen).
  const sectionBlocks = groups.map((g) => {
    const tableBody: any[] = [];
    let pending: any[] | null = null;
    for (const r of g.rows) {
      if (r.full) {
        if (pending) { pending.push({ text: '' }); tableBody.push(pending); pending = null; }
        tableBody.push([
          { stack: [
            { text: r.label, style: 'label' },
            { text: r.value, style: 'value' },
          ], colSpan: 2 },
          {},
        ]);
      } else {
        const cell = { stack: [
          { text: r.label, style: 'label' },
          { text: r.value, style: 'value' },
        ] };
        if (!pending) pending = [cell];
        else { pending.push(cell); tableBody.push(pending); pending = null; }
      }
    }
    if (pending) { pending.push({ text: '' }); tableBody.push(pending); }
    return {
      stack: [
        g.section
          ? { text: g.section.toUpperCase(), style: 'sectionHeader', margin: [0, 12, 0, 4] }
          : { text: '', margin: [0, 4, 0, 0] },
        {
          table: { widths: ['50%', '50%'], body: tableBody.length ? tableBody : [[{ text: '—', colSpan: 2 }, {}]] },
          layout: 'noBorders',
        },
      ],
      unbreakable: tableBody.length <= 4, // keep small sections together
    };
  });

  return {
    info: {
      title: `${config.singular} – ${subtitle}`,
      creator: BRAND.name,
    },
    pageSize: 'A4',
    pageMargins: [40, 60, 40, 50],
    defaultStyle: { font: 'Roboto', fontSize: 10, color: BRAND.primary },
    header: {
      margin: [40, 24, 40, 0],
      columns: [
        { text: BRAND.name, style: 'brand' },
        { text: `${config.singular} details`, alignment: 'right', style: 'docTitle' },
      ],
    },
    footer: (currentPage: number, pageCount: number) => ({
      margin: [40, 0, 40, 24],
      columns: [
        { text: `Generated ${generatedAt}`, style: 'footer' },
        { text: `Page ${currentPage} of ${pageCount}`, alignment: 'right', style: 'footer' },
      ],
    }),
    content: [
      { text: subtitle || `(${config.singular})`, style: 'subtitle' },
      { canvas: [{ type: 'line', x1: 0, y1: 4, x2: 515, y2: 4, lineWidth: 1.5, lineColor: BRAND.accent }] },
      ...sectionBlocks,
      ...buildChecklistBlock(extras?.checklistAnswers ?? []),
      ...buildPhotosBlock(extras?.photos ?? []),
    ],
    styles: {
      brand:      { fontSize: 9, color: BRAND.muted, characterSpacing: 0.5 },
      docTitle:   { fontSize: 9, color: BRAND.muted, characterSpacing: 0.5 },
      subtitle:   { fontSize: 18, bold: true, color: BRAND.primary, margin: [0, 0, 0, 0] },
      sectionHeader: { fontSize: 9, bold: true, color: BRAND.accent, characterSpacing: 0.6 },
      label:      { fontSize: 7.5, color: BRAND.muted, characterSpacing: 0.4, margin: [0, 0, 0, 1] },
      value:      { fontSize: 10, color: BRAND.primary, margin: [0, 0, 0, 6] },
      footer:     { fontSize: 8, color: BRAND.muted },
      tableHeader:{ fontSize: 8, bold: true, color: BRAND.muted, characterSpacing: 0.4 },
    },
  };
}

/** Render the checklist as a table: Task | Answer | Comments. Empty
 *  checklist returns no blocks so the PDF doesn't show a hanging
 *  section header. */
function buildChecklistBlock(answers: NonNullable<PdfExtras['checklistAnswers']>): any[] {
  if (answers.length === 0) return [];
  const rows: any[] = [
    [
      { text: 'Task',     style: 'tableHeader' },
      { text: 'Answer',   style: 'tableHeader' },
      { text: 'Comments', style: 'tableHeader' },
    ],
  ];
  for (const a of answers) {
    rows.push([
      { text: a.task ?? '—',     style: 'value' },
      { text: a.answer ?? '—',   style: 'value' },
      { text: a.comments ?? '—', style: 'value' },
    ]);
  }
  return [
    { text: 'CHECKLIST ITEMS', style: 'sectionHeader', margin: [0, 16, 0, 4] },
    {
      table: { headerRows: 1, widths: ['*', 80, '*'], body: rows },
      layout: {
        hLineWidth: (i: number) => (i === 0 || i === 1 ? 1 : 0.4),
        vLineWidth: () => 0,
        hLineColor: () => BRAND.border,
      },
    },
  ];
}

/** Render uploaded photos as a flowing grid — two per row at fitWidth
 *  half-page so the layout stays compact regardless of how many were
 *  attached. Captions sit below each image. */
function buildPhotosBlock(photos: NonNullable<PdfExtras['photos']>): any[] {
  if (photos.length === 0) return [];
  const PAIR_WIDTH = 245; // half the page content width, fitWidth respects it
  const pairs: any[] = [];
  for (let i = 0; i < photos.length; i += 2) {
    const a = photos[i];
    const b = photos[i + 1];
    const cell = (p: { caption: string; dataUrl: string } | undefined) => p
      ? { stack: [
          { image: p.dataUrl, fit: [PAIR_WIDTH, PAIR_WIDTH], alignment: 'left' as const },
          { text: p.caption, style: 'label', margin: [0, 4, 0, 0] },
        ] }
      : { text: '' };
    pairs.push({
      columns: [cell(a), cell(b)],
      columnGap: 12,
      margin: [0, 0, 0, 12],
    });
  }
  return [
    { text: 'PHOTOS', style: 'sectionHeader', margin: [0, 16, 0, 6] },
    ...pairs,
  ];
}

/** Public entrypoint — generates a PDF for the given record and
 *  triggers a download. Filename = "<Singular> - <subtitle>.pdf". */
export function downloadDetailPdf(config: CrudConfig, row: any, extras?: PdfExtras): void {
  const subtitle = rowSubtitle(row);
  const filename = safeFilename([config.singular, subtitle]) + '.pdf';
  const doc = buildDocDefinition(config, row, extras);
  pdfMake.createPdf(doc as any).download(filename);
}

// ---------------------------------------------------------------------------
// Job Card "View Service" PDF
//
// Mirrors what the View Service page (`job-card-service.tsx`) renders, minus
// Documents & Photos. Lives alongside the generic detail-pdf so it shares
// brand styling but bypasses the CrudConfig field walker — the View Service
// page composes several collections (JobCard + linked ServiceHistory + time
// logs + checklist answers), and the generic per-field grid can't express
// that layout.
// ---------------------------------------------------------------------------

export interface JobCardServicePdfData {
  jc: {
    jobSheetNumber?: string | null;
    status?: string | null;
    workDescription?: string | null;
    reviewNotes?: string | null;
    asset?: { fleetNumber?: string | null } | null;
    servicePlan?: { name?: string | null } | null;
    servicePlanSchedule?: { name?: string | null } | null;
    assignedTo?: Array<{ fullName?: string | null }> | null;
  };
  sh: {
    odometer?: number | null;
    engineHours?: number | null;
    hubo?: number | null;
    descriptionOfWork?: string | null;
    faultDescriptions?: string | null;
  } | null;
  timeLogs: Array<{
    startTime?: string | null;
    endTime?: string | null;
    hours?: number | null;
    employee?: { fullName?: string | null } | null;
  }>;
  checklistAnswers: Array<{
    task: string | null;
    answer: string | null;
    comments: string | null;
  }>;
  /** Human label for the workflow state — e.g. `jobCardStatusLabel(status)`. */
  statusLabel?: string;
}

function jobCardServiceDocDefinition(data: JobCardServicePdfData) {
  const { jc, sh, timeLogs, checklistAnswers, statusLabel } = data;
  const generatedAt = fmtDateTime(new Date());
  const subtitle = jc.jobSheetNumber || '';
  const assignedNames = (jc.assignedTo ?? [])
    .map((a) => a?.fullName)
    .filter((n): n is string => !!n)
    .join(', ') || '—';
  const totalHours = timeLogs.reduce((s, l) => s + (l.hours ?? 0), 0);

  // Two-column "label / value" cell, matching the generic detail-pdf grid.
  const pair = (label: string, value: string) => ({
    stack: [
      { text: label, style: 'label' },
      { text: value || '—', style: 'value' },
    ],
  });
  const fullPair = (label: string, value: string) => ({
    colSpan: 2,
    stack: [
      { text: label, style: 'label' },
      { text: value || '—', style: 'value' },
    ],
  });

  // ---------- Header / overview ----------
  const overviewBody: any[] = [
    [pair('Asset', jc.asset?.fleetNumber || '—'),  pair('Status', statusLabel || jc.status || '—')],
    [pair('Service Plan', jc.servicePlan?.name || '—'), pair('Schedule', jc.servicePlanSchedule?.name || '—')],
    [pair('Assigned To', assignedNames), pair('Total Hours Logged', `${totalHours.toFixed(2)} h`)],
  ];
  if (jc.workDescription) {
    overviewBody.push([fullPair('Work Description', jc.workDescription), {}]);
  }

  // ---------- Time tracking sessions ----------
  const sessionsBlock: any[] = [];
  if (timeLogs.length > 0) {
    const rows: any[] = [[
      { text: 'Technician', style: 'tableHeader' },
      { text: 'Start',      style: 'tableHeader' },
      { text: 'End',        style: 'tableHeader' },
      { text: 'Hours',      style: 'tableHeader' },
    ]];
    for (const l of timeLogs) {
      rows.push([
        { text: l.employee?.fullName || '—', style: 'value' },
        { text: l.startTime ? fmtDateTime(l.startTime) : '—', style: 'value' },
        { text: l.endTime   ? fmtDateTime(l.endTime)   : 'open', style: 'value' },
        { text: l.hours != null ? `${l.hours} h` : '—', style: 'value' },
      ]);
    }
    sessionsBlock.push(
      { text: 'TIME TRACKING', style: 'sectionHeader', margin: [0, 16, 0, 4] },
      {
        table: { headerRows: 1, widths: ['*', 'auto', 'auto', 'auto'], body: rows },
        layout: {
          hLineWidth: (i: number) => (i === 0 || i === 1 ? 1 : 0.4),
          vLineWidth: () => 0,
          hLineColor: () => BRAND.border,
        },
      },
    );
  }

  // ---------- Service details (from linked ServiceHistory) ----------
  // Meters render in a 3-up row (matches the View Service page's
  // `md:grid-cols-3`); description / fault blocks sit beneath in their own
  // full-width single-column table so the long text isn't constrained to a
  // third of the page width.
  const serviceDetailsBlock: any[] = [];
  if (sh) {
    const fmtNum = (n: number | null | undefined) =>
      typeof n === 'number' ? n.toLocaleString() : '—';
    serviceDetailsBlock.push(
      { text: 'SERVICE DETAILS', style: 'sectionHeader', margin: [0, 16, 0, 4] },
      {
        table: {
          widths: ['*', '*', '*'],
          body: [[
            pair('Odometer',     fmtNum(sh.odometer)),
            pair('Engine Hours', fmtNum(sh.engineHours)),
            pair('Hubo',         fmtNum(sh.hubo)),
          ]],
        },
        layout: 'noBorders',
      },
    );
    const descRows: any[] = [];
    if (sh.descriptionOfWork) descRows.push([pair('Description of Work', sh.descriptionOfWork)]);
    if (sh.faultDescriptions) descRows.push([pair('Fault Descriptions', sh.faultDescriptions)]);
    if (descRows.length > 0) {
      serviceDetailsBlock.push({
        table: { widths: ['*'], body: descRows },
        layout: 'noBorders',
        margin: [0, 4, 0, 0],
      });
    }
  }

  // ---------- Review notes (only when filled) ----------
  const reviewBlock: any[] = [];
  if (jc.reviewNotes) {
    reviewBlock.push(
      { text: 'REVIEW', style: 'sectionHeader', margin: [0, 16, 0, 4] },
      {
        // Single-column table — use a plain `pair` (no colSpan) so the row
        // length matches the `widths` array; a colSpan:2 cell here would
        // make pdfmake walk past the row end and crash in `getMinMax`.
        table: { widths: ['*'], body: [[pair('Notes', jc.reviewNotes)]] },
        layout: 'noBorders',
      },
    );
  }

  return {
    info: { title: `Job Card – ${subtitle}`, creator: BRAND.name },
    pageSize: 'A4',
    pageMargins: [40, 60, 40, 50],
    defaultStyle: { font: 'Roboto', fontSize: 10, color: BRAND.primary },
    header: {
      margin: [40, 24, 40, 0],
      columns: [
        { text: BRAND.name, style: 'brand' },
        { text: 'Job Card details', alignment: 'right', style: 'docTitle' },
      ],
    },
    footer: (currentPage: number, pageCount: number) => ({
      margin: [40, 0, 40, 24],
      columns: [
        { text: `Generated ${generatedAt}`, style: 'footer' },
        { text: `Page ${currentPage} of ${pageCount}`, alignment: 'right', style: 'footer' },
      ],
    }),
    content: [
      { text: subtitle || '(Job Card)', style: 'subtitle' },
      { canvas: [{ type: 'line', x1: 0, y1: 4, x2: 515, y2: 4, lineWidth: 1.5, lineColor: BRAND.accent }] },
      { text: 'OVERVIEW', style: 'sectionHeader', margin: [0, 12, 0, 4] },
      { table: { widths: ['50%', '50%'], body: overviewBody }, layout: 'noBorders' },
      ...sessionsBlock,
      ...serviceDetailsBlock,
      ...buildChecklistBlock(checklistAnswers),
      ...reviewBlock,
    ],
    styles: {
      brand:      { fontSize: 9, color: BRAND.muted, characterSpacing: 0.5 },
      docTitle:   { fontSize: 9, color: BRAND.muted, characterSpacing: 0.5 },
      subtitle:   { fontSize: 18, bold: true, color: BRAND.primary, margin: [0, 0, 0, 0] },
      sectionHeader: { fontSize: 9, bold: true, color: BRAND.accent, characterSpacing: 0.6 },
      label:      { fontSize: 7.5, color: BRAND.muted, characterSpacing: 0.4, margin: [0, 0, 0, 1] },
      value:      { fontSize: 10, color: BRAND.primary, margin: [0, 0, 0, 6] },
      footer:     { fontSize: 8, color: BRAND.muted },
      tableHeader:{ fontSize: 8, bold: true, color: BRAND.muted, characterSpacing: 0.4 },
    },
  };
}

/** Public entrypoint for the View Service PDF — caller pre-fetches the
 *  JobCard, linked ServiceHistory, time logs, and checklist answers (the
 *  exact same collections the View Service page queries). Filename:
 *  "Job Card - <jobSheetNumber>.pdf". */
export function downloadJobCardServicePdf(data: JobCardServicePdfData): void {
  const filename = safeFilename(['Job Card', data.jc.jobSheetNumber || '']) + '.pdf';
  const doc = jobCardServiceDocDefinition(data);
  pdfMake.createPdf(doc as any).download(filename);
}
